import React, { useState } from 'react';
import { useMsal } from '@azure/msal-react';
import { useAuth } from '../auth/AuthContext';
import { loginRequest, isAzureAdConfigured } from '../auth/msalConfig';

const LoginPage: React.FC = () => {
  const { loginLocal, loginAzure } = useAuth();
  const { instance: msalInstance } = useMsal();

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleLocalLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!username || !password) {
      setError('Please enter username and password.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      await loginLocal(username, password);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleMicrosoftLogin = async () => {
    setLoading(true);
    setError('');
    try {
      const result = await msalInstance.loginPopup(loginRequest);
      if (result.idToken) {
        await loginAzure(result.idToken);
      }
    } catch (err: any) {
      if (err.errorCode !== 'user_cancelled') {
        setError(err.message || 'Microsoft sign-in failed.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <style>{`
        .login-page {
          min-height: 100vh;
          display: flex;
          align-items: center;
          justify-content: center;
          background-color: #1a1a1e;
          padding: 1rem;
        }

        .login-card {
          background-color: #25252a;
          border: 1px solid #444;
          border-radius: 10px;
          padding: 2.5rem;
          width: 100%;
          max-width: 400px;
          box-shadow: 0 8px 32px rgba(0, 0, 0, 0.4);
        }

        .login-logo {
          text-align: center;
          margin-bottom: 2rem;
        }

        .login-logo h1 {
          color: #f0f0f0;
          font-size: 1.5rem;
          margin: 0 0 0.25rem;
        }

        .login-logo p {
          color: #b0b0b0;
          font-size: 0.9rem;
          margin: 0;
        }

        .login-logo .logo-icon {
          font-size: 2.5rem;
          margin-bottom: 0.75rem;
        }

        .login-form .form-group {
          margin-bottom: 1rem;
        }

        .login-form label {
          display: block;
          color: #b0b0b0;
          font-size: 0.875rem;
          margin-bottom: 0.35rem;
        }

        .login-form input {
          width: 100%;
          background-color: #1a1a1e;
          color: #f0f0f0;
          border: 1px solid #444;
          border-radius: 6px;
          padding: 0.65rem 0.85rem;
          font-size: 1rem;
          box-sizing: border-box;
          transition: border-color 0.2s;
        }

        .login-form input:focus {
          outline: none;
          border-color: #007bff;
        }

        .btn-login {
          width: 100%;
          padding: 0.75rem;
          font-size: 1rem;
          font-weight: 600;
          border: none;
          border-radius: 6px;
          cursor: pointer;
          transition: all 0.2s;
          margin-top: 0.5rem;
        }

        .btn-login:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .btn-local {
          background-color: #007bff;
          color: white;
        }

        .btn-local:hover:not(:disabled) {
          background-color: #0056b3;
        }

        .divider {
          display: flex;
          align-items: center;
          gap: 1rem;
          margin: 1.5rem 0;
          color: #666;
          font-size: 0.875rem;
        }

        .divider::before,
        .divider::after {
          content: '';
          flex: 1;
          height: 1px;
          background-color: #444;
        }

        .btn-microsoft {
          background-color: #2c2c31;
          color: #f0f0f0;
          border: 1px solid #555;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.75rem;
        }

        .btn-microsoft:hover:not(:disabled) {
          background-color: #3a3a40;
          border-color: #007bff;
        }

        .btn-microsoft .ms-logo {
          width: 20px;
          height: 20px;
          flex-shrink: 0;
        }

        .btn-microsoft-disabled {
          background-color: #2c2c31;
          color: #666;
          border: 1px solid #444;
          cursor: not-allowed;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.75rem;
        }

        .azure-not-configured {
          font-size: 0.75rem;
          color: #888;
          text-align: center;
          margin-top: 0.5rem;
        }

        .login-error {
          background-color: #dc354522;
          border: 1px solid #dc3545;
          color: #ff8f9a;
          border-radius: 6px;
          padding: 0.75rem 1rem;
          font-size: 0.875rem;
          margin-bottom: 1rem;
        }

        .demo-hint {
          background-color: #007bff11;
          border: 1px solid #007bff44;
          color: #90c4ff;
          border-radius: 6px;
          padding: 0.6rem 1rem;
          font-size: 0.8rem;
          text-align: center;
          margin-top: 1.5rem;
        }
      `}</style>

      <div className="login-page">
        <div className="login-card">
          <div className="login-logo">
            <div className="logo-icon">🏷️</div>
            <h1>Azure Tag Manager</h1>
            <p>Sign in to manage your Azure resource tags</p>
          </div>

          {error && <div className="login-error">{error}</div>}

          <form className="login-form" onSubmit={handleLocalLogin}>
            <div className="form-group">
              <label htmlFor="username">Username</label>
              <input
                id="username"
                type="text"
                value={username}
                onChange={e => setUsername(e.target.value)}
                placeholder="Enter username"
                disabled={loading}
                autoComplete="username"
              />
            </div>
            <div className="form-group">
              <label htmlFor="password">Password</label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                placeholder="Enter password"
                disabled={loading}
                autoComplete="current-password"
              />
            </div>
            <button
              type="submit"
              className="btn-login btn-local"
              disabled={loading}
            >
              {loading ? 'Signing in...' : 'Sign in'}
            </button>
          </form>

          <div className="divider">or</div>

          {isAzureAdConfigured ? (
            <button
              className="btn-login btn-microsoft"
              onClick={handleMicrosoftLogin}
              disabled={loading}
              type="button"
            >
              <svg className="ms-logo" viewBox="0 0 21 21" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect x="1" y="1" width="9" height="9" fill="#F25022"/>
                <rect x="11" y="1" width="9" height="9" fill="#7FBA00"/>
                <rect x="1" y="11" width="9" height="9" fill="#00A4EF"/>
                <rect x="11" y="11" width="9" height="9" fill="#FFB900"/>
              </svg>
              Sign in with Microsoft
            </button>
          ) : (
            <>
              <button className="btn-login btn-microsoft-disabled" disabled type="button">
                <svg className="ms-logo" viewBox="0 0 21 21" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ opacity: 0.4 }}>
                  <rect x="1" y="1" width="9" height="9" fill="#F25022"/>
                  <rect x="11" y="1" width="9" height="9" fill="#7FBA00"/>
                  <rect x="1" y="11" width="9" height="9" fill="#00A4EF"/>
                  <rect x="11" y="11" width="9" height="9" fill="#FFB900"/>
                </svg>
                Sign in with Microsoft
              </button>
              <p className="azure-not-configured">Azure AD not configured yet</p>
            </>
          )}

          <div className="demo-hint">
            Demo access: <strong>test</strong> / <strong>test</strong>
          </div>
        </div>
      </div>
    </>
  );
};

export default LoginPage;
